import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, useRef, useState } from "react";
import { offset, flip, shift, autoUpdate, useFloating, useHover, useInteractions, useRole, useDismiss, useClick, FloatingFocusManager, arrow, safePolygon, FloatingPortal, } from "@floating-ui/react";
import classNames from "classnames";
import { ThemeProviderContext } from "./ThemeProvider";
export const Popover = ({ trigger, content, placement, triggerAction = "click", initialFocus = true, withArrow = false, withPadding = true, withShadow = true, invertColors = false, closeOnClickAway = true, className = "", openDelay = 0, closeDelay = 200, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const arrowRef = useRef(null);
    const { x, y, reference, floating, strategy, context, placement: placementData, middlewareData: { arrow: { x: arrowX, y: arrowY } = {} }, } = useFloating({
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(6), flip(), shift(), arrow({ element: arrowRef })],
        placement,
        strategy: "fixed",
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context, { enabled: triggerAction === "click" }),
        useRole(context),
        useDismiss(context, { enabled: closeOnClickAway }),
        useHover(context, {
            enabled: triggerAction === "hover",
            handleClose: safePolygon({
                blockPointerEvents: false,
            }),
            delay: {
                open: openDelay,
                close: closeDelay,
            },
        }),
    ]);
    const close = () => setIsOpen(false);
    const arrowSide = placementData.split("-")[0];
    const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
    }[arrowSide] || "";
    const borderColor = (borderSide) => borderSide === arrowSide ? "" : "transparent";
    const GRID_INTERVAL = 8;
    const arrowStyle = {
        left: arrowX != null ? arrowX : "",
        top: arrowY != null ? arrowY : "",
        right: "",
        bottom: "",
        borderWidth: GRID_INTERVAL,
        borderStyle: "solid",
        borderTopColor: borderColor("top"),
        borderRightColor: borderColor("right"),
        borderBottomColor: borderColor("bottom"),
        borderLeftColor: borderColor("left"),
        [staticSide]: GRID_INTERVAL * -2,
    };
    if (typeof window === "undefined") {
        return null;
    }
    return (_jsx(ThemeProviderContext.Consumer, { children: ({ menuZIndex }) => (_jsxs(_Fragment, { children: [cloneElement(trigger, getReferenceProps(Object.assign({ ref: reference }, trigger.props))), _jsx(FloatingPortal, { root: document.body, children: isOpen && (_jsx(FloatingFocusManager, { context: context, initialFocus: initialFocus ? 0 : -1, returnFocus: false, children: _jsxs("div", Object.assign({ onClick: (e) => e.stopPropagation() }, getFloatingProps({
                            className: classNames("rounded", "border", "border-solid", "border-border-subdued", "focus:outline-0", withPadding && ["px-1", "py-0.5"], withShadow && ["drop-shadow", "dark:drop-shadow-dark"], invertColors
                                ? [
                                    "bg-background-primary-darkmode",
                                    "dark:bg-background-primary",
                                    "border-border-subdued-darkmode",
                                    "text-text-primary-darkmode",
                                    "dark:text-text-primary",
                                ]
                                : [
                                    "bg-background-primary",
                                    "dark:bg-background-tertiary-darkmode",
                                    "dark:border-border-subdued-darkmode",
                                ], className),
                            ref: floating,
                            style: {
                                position: strategy,
                                top: y !== null && y !== void 0 ? y : "",
                                left: x !== null && x !== void 0 ? x : "",
                                zIndex: menuZIndex,
                            },
                        }), { children: [content({ close }), withArrow && (_jsx("div", { ref: arrowRef, style: arrowStyle, className: classNames("absolute", invertColors
                                        ? [
                                            "dark:border-background-primary",
                                            "border-background-tertiary-darkmode",
                                        ]
                                        : [
                                            "border-background-primary",
                                            "dark:border-background-tertiary-darkmode",
                                        ]) }))] })) })) })] })) }));
};
//# sourceMappingURL=Popover.js.map